import { createSelector } from '@reduxjs/toolkit';
import { selectVariantEntities } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { selectAllGroups, selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { selectAllPhotos, selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { sum } from 'lodash';

export const selectAmountInQueueByGroupId = createSelector([
    selectGroupById
], g => g!.amountInQueue);

export const selectIsEmptyGroup = createSelector([
    selectPhotoIdsByGroupId,
    selectAmountInQueueByGroupId
], (ids, amountInQueue) => ids.length === 0 && amountInQueue === 0);

export const selectTotalAmountInQueue = createSelector([
    selectAllGroups
], (groups) => sum(groups.map(g => g.amountInQueue)));

export const selectIsAnyGroupNotEmpty = createSelector([
    selectAllPhotos,
    selectTotalAmountInQueue
], (photos, amountInQueue) => {
    return photos.length > 0
        || amountInQueue > 0;
});

export const selectAmountOfUploads = createSelector([
    (state: PhotoPrintsPdpState) => selectAllGroups(state),
    (state: PhotoPrintsPdpState) => selectAllPhotos(state)
], (groups, photos) => {

    const amountInQueue = sum(groups.map(g => g.amountInQueue));
    const amountOfPhotos = photos.length;

    return amountInQueue + amountOfPhotos;
});

export const selectHaveAnyPhotosBeenAdded = createSelector([
    selectAmountOfUploads
], (amountOfUploads) => amountOfUploads > 0);

export const selectCanRemoveGroup = createSelector([
    selectIsEmptyGroup,
    selectAllGroups
], (isEmpty, groups) => {
    return isEmpty && groups.length > 1;
});

export const selectTotalRequest = createSelector([
    selectAllGroups,
    selectAllPhotos,
    selectVariantEntities
], (groups, photos, variants) => ({
    items: groups.map(g => ({
        sku: variants[g.variantId]!.sku,
        quantity: sum(photos
            .filter(p => p.groupId === g.id)
            .map(p => p.quantity))
    }))
}));

const selectActiveVariantId = createSelector([
    selectGroupById
], group => group!.variantId);

export const selectActiveVariant = createSelector([
    selectActiveVariantId,
    selectVariantEntities
], (activeVariantId, allVariants) => allVariants[activeVariantId]!);